<template>
  <component
    :is="withContainer ? 'ChiftContainer' : 'div'"
    v-if="isVisible"
    v-bind="withContainer ? { size: 'extra-small' } : {}"
    :class="{ contents: isCompact && !withContainer }"
  >
    <!--
      Compact: only the diagnostics icon, pinned on the Chift logo row. The
      container wraps its content in a relative box, so bottom-full lifts the
      icon out of the content flow, whether the invoice was sent or not.
    -->
    <div v-if="isCompact" class="absolute right-0 bottom-full mb-1.5 text-xs">
      <PaymentSyncDiagnosticsPopover
        :diagnostics="invoice.payments_sync_diagnostics"
      />
    </div>
    <!--
      Laid out for a 256px column: the label wraps, the button spans the width and
      the icons sit on the status line rather than beside the button, so the
      interrupted state never overflows.
    -->
    <div v-else class="flex flex-col gap-1.5 text-xs">
      <div class="flex items-start gap-1.5">
        <FontAwesomeIcon
          :icon="['fas', statusIcon]"
          class="mt-0.5"
          :class="{
            'text-green-500': isEnabled && !isAwaitingSend,
            'text-blue-500': isAwaitingSend,
            'text-amber-500': isInterrupted,
            'text-gray-400': isDisabled
          }"
        />
        <span class="flex flex-1 flex-col">
          <span class="font-medium text-gray-700 capitalize-first">
            {{ statusLabel }}
          </span>
          <span
            v-if="interruptionReasonLabel"
            class="text-gray-500 capitalize-first"
          >
            {{ interruptionReasonLabel }}
          </span>
        </span>
        <PaymentSyncDiagnosticsPopover
          v-if="invoice.payments_sync_diagnostics"
          class="shrink-0"
          :diagnostics="invoice.payments_sync_diagnostics"
        />
        <button
          type="button"
          class="shrink-0 text-gray-400 hover:text-gray-600"
          :title="$t('invoicing.payment_sync_invoice_history')"
          @click="showHistory = true"
        >
          <FontAwesomeIcon :icon="['fal', 'clock-rotate-left']" />
        </button>
      </div>

      <template v-if="isInterrupted || isDisabledButReactivatable">
        <BaseButton
          :title="$t('invoicing.payments_sync_reenable')"
          :disabled="!canReenable || loading"
          color="primary"
          size="extra-small"
          class="w-full"
          @click="reenable"
        />
        <p v-if="!canReenable" class="text-amber-700 capitalize-first">
          {{ $t('invoicing.payments_sync_reenable_blocked') }}
        </p>
      </template>
    </div>

    <ChiftPaymentSyncRunModal
      v-if="showHistory"
      :invoice-id="invoice.id"
      @close="showHistory = false"
    />
  </component>
</template>

<script>
import { mapActions } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import ChiftContainer from '@c/Partner/Chift/ChiftContainer.vue'
import ChiftPaymentSyncRunModal from '@c/Partner/Chift/ChiftPaymentSyncRunModal.vue'
import PaymentSyncDiagnosticsPopover from './PaymentSyncDiagnosticsPopover.vue'
import { paymentSyncPartnerName } from '@c/Partner/Chift/paymentSyncPartner'

export default {
  name: 'InvoicingPaymentSyncBanner',
  components: {
    FontAwesomeIcon,
    BaseButton,
    ChiftContainer,
    ChiftPaymentSyncRunModal,
    PaymentSyncDiagnosticsPopover
  },
  props: {
    invoice: {
      type: Object,
      required: true
    },
    /**
     * False when rendered inside the accounting block that already provides the
     * Chift container, so the partner frame and logo are not drawn twice.
     */
    withContainer: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      showHistory: false
    }
  },
  computed: {
    status() {
      return this.invoice.payments_sync_status
    },
    isEnabled() {
      return this.status === 'ENA'
    },
    isInterrupted() {
      return this.status === 'INT'
    },
    isDisabled() {
      return this.status === 'DIS'
    },
    /**
     * Company side of the opt-in: SyncChiftPayments only polls companies whose
     * own Chift Accounting connection is enabled, or whose Billit payment sync
     * is switched on.
     */
    isCompanyOptedIn() {
      return (
        this.invoice.company?.chift_accounting_enabled === true ||
        this.invoice.company?.chift_invoicing_payment_sync_enabled === true
      )
    },
    sentToPartnerCheck() {
      return (
        this.invoice.payments_sync_diagnostics?.find(
          (check) => check.code === 'sent_to_partner'
        ) ?? null
      )
    },
    /**
     * Followed, but the partner has not received the invoice yet (Billit's
     * daily send, or a send that failed): nothing can be synced back until it
     * is there, which "active" alone would hide.
     */
    isAwaitingSend() {
      return this.isEnabled && this.sentToPartnerCheck?.ok === false
    },
    /**
     * A DISABLED invoice of a company that never opted into the payment sync
     * is the normal state of nearly every invoice on the tenant: spelling it
     * out on each of them would be noise, so only the diagnostics icon stays,
     * for whoever wonders why. Once the company opted in, DISABLED is the
     * exception worth a full banner - as are ENABLED and INTERRUPTED, always.
     * A reactivatable invoice keeps the full banner too, or its reactivate
     * button would vanish behind the icon (the backend already requires the
     * company connection for it; this only guards the display).
     */
    isCompact() {
      return (
        this.isDisabled &&
        !this.isCompanyOptedIn &&
        !this.isDisabledButReactivatable
      )
    },
    isVisible() {
      return !this.isCompact || Boolean(this.invoice.payments_sync_diagnostics)
    },
    statusIcon() {
      if (this.isAwaitingSend) {
        return 'clock'
      }

      if (this.isEnabled) {
        return 'circle-check'
      }

      if (this.isInterrupted) {
        return 'triangle-exclamation'
      }

      return 'circle-stop'
    },
    /**
     * A DISABLED invoice only gets the reactivate button for the narrow case
     * PaymentSyncActivator::activateOnEstablish() cannot retry on its own: it
     * left DRAFT just before its diary's Chift connection was enabled. Most
     * DISABLED invoices belong to a journal never opted into Chift at all -
     * for those, can_reenable_payments_sync is omitted or false, and no
     * button (which would fix nothing) is shown; only the diagnostics icon is.
     */
    isDisabledButReactivatable() {
      return this.isDisabled && this.invoice.can_reenable_payments_sync === true
    },
    canReenable() {
      return this.invoice.can_reenable_payments_sync !== false
    },
    /**
     * Why the sync stopped, reusing the diagnostics wording: a payment encoded
     * by hand (the user's choice) and a payment changed on the partner's side
     * call for different follow-ups.
     */
    interruptionReasonLabel() {
      if (!this.isInterrupted) {
        return null
      }

      // A row interrupted before the reason existed has none: say so, like
      // the diagnostics popover does, rather than showing nothing.
      const reason = this.invoice.payments_sync_interruption_reason ?? 'unknown'

      return this.$t(
        `invoicing.payments_sync_diagnostic_interruption_reason_${reason}`
      )
    },
    statusLabel() {
      if (this.isAwaitingSend) {
        return this.$t('invoicing.payments_sync_awaiting_send', {
          partner: paymentSyncPartnerName(this.sentToPartnerCheck.partner)
        })
      }

      if (this.isEnabled) {
        return this.$t('invoicing.payments_sync_active')
      }

      if (this.isInterrupted) {
        return this.$t('invoicing.payments_sync_interrupted_banner', {
          date: this.$h.formatDate(
            this.invoice.payments_sync_changed_at,
            'D MMM YYYY HH:mm',
            this.$i18n.locale.substring(0, 2)
          )
        })
      }

      return this.$t('invoicing.payments_sync_disabled_banner')
    }
  },
  methods: {
    ...mapActions({
      processEnableInvoicePaymentsSyncAction:
        'invoicing/enableInvoicePaymentsSync',
      processFetchInvoiceByIdAction: 'invoicing/fetchInvoiceID'
    }),
    reenable() {
      if (!this.canReenable) {
        return
      }

      this.loading = true
      this.processEnableInvoicePaymentsSyncAction({
        invoice_id: this.invoice.id
      })
        .then(() => {
          this.processFetchInvoiceByIdAction({ id: this.invoice.id })
        })
        .catch((reason) => {
          this.$h.toastDanger(
            reason.response?.data?.message ??
              this.$t('toasts.something_went_wrong')
          )
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
